import { useState } from 'react'
import { ApiError } from '../api/client'
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '../features/users/preferences'

/**
 * Where the unsubscribe link in every reminder lands.
 *
 * The two kinds are independent: someone who wants to be left alone about work
 * that is merely approaching usually still wants telling when it is late.
 */
export function NotificationPreferencesPage() {
  const { data: preferences, isPending, isError } = useNotificationPreferences()
  const update = useUpdateNotificationPreferences()
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  if (isPending) return <p>Loading your preferences…</p>
  if (isError || !preferences) {
    return (
      <p role="alert" className="error">
        Could not load your notification preferences.
      </p>
    )
  }

  const save = async (next: { upcoming: boolean; overdue: boolean }) => {
    setError(null)
    setSaved(false)
    try {
      await update.mutateAsync({
        upcomingRemindersEnabled: next.upcoming,
        overdueRemindersEnabled: next.overdue,
      })
      setSaved(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Could not save your preferences.')
    }
  }

  return (
    <section>
      <h1>Email reminders</h1>
      <p className="muted">
        Reminders are sent once a day for work assigned to you. These settings are yours alone and
        do not affect anyone else.
      </p>

      {error ? (
        <p role="alert" className="error">
          {error}
        </p>
      ) : null}
      {saved ? <p className="notice">Saved.</p> : null}

      <div className="form">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={preferences.upcomingRemindersEnabled}
            onChange={(event) =>
              void save({
                upcoming: event.target.checked,
                overdue: preferences.overdueRemindersEnabled,
              })
            }
          />
          Tell me when work is coming due
        </label>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={preferences.overdueRemindersEnabled}
            onChange={(event) =>
              void save({
                upcoming: preferences.upcomingRemindersEnabled,
                overdue: event.target.checked,
              })
            }
          />
          Tell me when work is overdue
        </label>

        <p className="hint">
          Turning both off stops all reminder email. Your assigned work is still listed under{' '}
          <strong>My work</strong>.
        </p>
      </div>
    </section>
  )
}
